import { h, Host, getElement, } from "@stencil/core";
import { delegateFocus, trapFocus } from "@eventstore-ui/utils";
import { ICON_NAMESPACE } from "../../icons/namespace";
/**
 * A pop up modal for overlaying information, warnings and confirmations.
 * Traps focus within the modal, and returns focus to previous location when closed.
 * Pair with an [`es-portal`](/components/components/es-portal) to open and close.
 * @part header - The modal header
 * @part body - The modal body
 * @part footer - The modal footer
 * @slot - Places components in the body.
 * @slot header - Places components in the header. Pass a h2 then a h1 for standard styling.
 * @slot footer - Places components in the footer. Pass es-button and es-button-link.
 */
export class Modal {
  constructor() {
    this.header = true;
    this.footer = true;
  }
  componentDidLoad() {
    delegateFocus(getElement(this));
  }
  connectedCallback() {
    this.releaseFocus = trapFocus(getElement(this));
  }
  disconnectedCallback() {
    this.releaseFocus?.();
  }
  onEscape(e) {
    if (e.key !== 'Escape')
      return;
    this.requestClose.emit();
  }
  render() {
    return (h(Host, null, h("button", { class: 'close', role: 'button', onClick: () => this.requestClose.emit(), "data-skip-focus-delegation": true }, h("es-icon", { icon: [ICON_NAMESPACE, 'close'], size: 22 })), this.header && (h("header", { part: 'header' }, h("slot", { name: 'header' }))), h("div", { class: 'body', part: 'body' }, h("slot", null)), this.footer && (h("footer", { part: 'footer' }, h("slot", { name: 'footer' })))));
  }
  static get is() { return "es-modal"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-modal.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-modal.css"]
    };
  }
  static get properties() {
    return {
      "header": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the modal should have a header."
        },
        "attribute": "header",
        "reflect": false,
        "defaultValue": "true"
      },
      "footer": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If the modal should have a footer."
        },
        "attribute": "footer",
        "reflect": false,
        "defaultValue": "true"
      }
    };
  }
  static get events() {
    return [{
        "method": "requestClose",
        "name": "requestClose",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggers when the modal requests to be closed."
        },
        "complexType": {
          "original": "void",
          "resolved": "void",
          "references": {}
        }
      }];
  }
  static get listeners() {
    return [{
        "name": "keyup",
        "method": "onEscape",
        "target": undefined,
        "capture": false,
        "passive": false
      }];
  }
}
//# sourceMappingURL=es-modal.js.map
